import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

type FakeScrollIndicatorProps = {
  /** Time in milliseconds to wait before showing the indicator. */
  showAfter?: number;
  /** Set to true when the user starts scrolling or touching the scroll view. */
  userHasInteracted?: boolean;
};

const FakeScrollIndicator = ({
  showAfter = 0,
  userHasInteracted = false,
}: FakeScrollIndicatorProps) => {
  const opacity = useSharedValue(0);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (userHasInteracted) {
      cancelAnimation(opacity);
      opacity.value = withTiming(0, { duration: 250 });
    } else {
      timeout = setTimeout(() => {
        opacity.value = withSequence(
          withTiming(1, { duration: 500, easing: Easing.inOut(Easing.ease) }),
          withDelay(
            1000,
            withTiming(0, { duration: 500, easing: Easing.inOut(Easing.ease) }),
          ),
        );
      }, showAfter);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [showAfter, opacity, userHasInteracted]);

  const indicatorStyle = useAnimatedStyle(() => {
    return {
      opacity: opacity.value,
    };
  });

  return (
    <View style={styles.container}>
      <Animated.View style={[styles.scrollIndicator, indicatorStyle]} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 2,
    right: 2,
    bottom: 0,
    width: 2.5,
    zIndex: 1,
  },
  scrollIndicator: {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: 2,
    width: '100%',
    height: '40%',
  },
});

export default FakeScrollIndicator;
